import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { PiniaSharedState } from 'pinia-shared-state'
import { createApp, defineAsyncComponent } from 'vue'
import './assets/styles/index.css'
import Toast from 'vue-toastification'
import router from './router'
import { configureHttp, restoreSession } from './lib/http'
import App from './views/App.vue'

// CSS Imports
import '@programic/vue3-tooltip/dist/index.css'
import 'vue-toastification/dist/index.css'

// Helpers
import {
    getCountries,
    getRanks,
    getScoreboardTypes,
    getTimeslots,
    getTimezones
} from './assets/js/data'

// Components
import Button from './components/App/Helpers/Button.vue'
import ColourPicker from './components/App/Helpers/ColourPicker.vue'
const Dropdown = defineAsyncComponent(() => import('./components/App/Helpers/Dropdown.vue'))
const InfiniteMulti = defineAsyncComponent(
    () => import('./components/App/Helpers/InfiniteMulti.vue')
)
import Link from './components/App/Helpers/Link.vue'
const Modal = defineAsyncComponent(() => import('./components/App/Helpers/Modal.vue'))
const Multi = defineAsyncComponent(() => import('./components/App/Helpers/Multi.vue'))
import Switch from './components/App/Helpers/Switch.vue'
import TextInput from './components/App/Helpers/TextInput.vue'
import TextPreview from './components/App/Helpers/TextPreview.vue'
import Timeline from './components/App/Helpers/Timeline.vue'

// axios defaults + 401 interceptor, before anything can make a request.
configureHttp()

const app = createApp(App)

// Error reporting, only when a DSN is configured (production sets
// VITE_SENTRY_DSN in CI). Loaded on demand so the SDK stays out of the bundle
// for local builds, and the Vite plugin's source-map upload keys off the same
// decision via SENTRY_AUTH_TOKEN.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN
if (sentryDsn) {
    import('@sentry/vue').then((Sentry) => {
        Sentry.init({
            app,
            dsn: sentryDsn,
            environment: import.meta.env.MODE,
            tracesSampleRate: 0
        })
    })
}

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
pinia.use(
    PiniaSharedState({
        enable: true,
        initialize: false,
        type: 'localstorage'
    })
)

app.component('font-awesome-icon', FontAwesomeIcon)
app.component('custom-link', Link)
app.component('text-input', TextInput)
app.component('colour-picker', ColourPicker)
app.component('custom-button', Button)
app.component('text-preview', TextPreview)
app.component('multi-select', Multi)
app.component('infinite-multi-select', InfiniteMulti)
app.component('dropdown', Dropdown)
app.component('timeline', Timeline)
app.component('modal', Modal)
app.component('custom-switch', Switch)

app.provide('scoreboardTypes', getScoreboardTypes())
app.provide('timeslots', getTimeslots())
app.provide('timezones', getTimezones())
app.provide('countries', getCountries())
app.provide('ranks', getRanks())

// Bottom-right so toasts never cover a page's header actions (the default
// top-right sat directly on top of the table toolbars).
app.use(Toast, {
    position: 'bottom-right',
    timeout: 4000,
    closeOnClick: true,
    pauseOnHover: true,
    hideProgressBar: false,
    transition: 'Vue-Toastification__fade'
})
/*
 * Do not retry a 4xx.
 *
 * vue-query's default is three retries with backoff, which is right for a
 * dropped connection and wrong for every client error: a 404 route, a 403, a
 * 422 validation failure will all fail identically on the fourth attempt. The
 * cost was about seven and a half seconds of spinner before any screen in the
 * app would admit something had gone wrong — measured against a missing
 * endpoint, which is exactly the case a developer hits most.
 *
 * 5xx and network failures still retry, because those genuinely are transient.
 */
app.use(VueQueryPlugin, {
    queryClientConfig: {
        defaultOptions: {
            queries: {
                retry: (failureCount: number, error: unknown) => {
                    const status = (error as { response?: { status?: number } })?.response?.status

                    if (status && status >= 400 && status < 500) return false

                    return failureCount < 3
                }
            }
        }
    }
})
app.use(pinia)

// Needs pinia installed before stores are used.
restoreSession()

app.use(router)
app.mount('#app')
